import type { MillsheetsSearchInput, SteelOrderQualityCheckStatusEnum, SteelOrdersSearchInput } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { buildShapeNameConversionDisplayMap, isPresent, isSome } from '@mbfab/shared'
import { chunk, range, sortBy } from 'es-toolkit'
import { Upload } from 'lucide-react'
import { useMemo, useState } from 'react'

import { GraphqlErrorPanel } from '@/components/app/graphql-error-panel'
import {
  EMPTY_MILLSHEET_SEARCH,
  parseMillsheetSearch,
  toMillsheetSortInputs,
} from '@/components/screens/millsheet/filter-state'
import { EMPTY_MILLSHEET_FILTER_OPTIONS } from '@/components/screens/millsheet/millsheet-home-container'
import type { MillsheetSearch } from '@/components/screens/millsheet/millsheets-table'
import type { SteelOrderSearchState } from '@/components/screens/order-matching/filter-bar'
import {
  EMPTY_STEEL_ORDER_SEARCH_STATE,
  FilterBar,
  parseSteelOrderSearchState,
} from '@/components/screens/order-matching/filter-bar'
import { ImportHistoryTable } from '@/components/screens/order-matching/import-history-table'
import type { SteelOrderBulkTarget } from '@/components/screens/order-matching/orders-table'
import { OrdersTable, toSteelOrderSorts } from '@/components/screens/order-matching/orders-table'
import type { S3ObjectForPrint, SteelOrderForPrint } from '@/components/screens/order-matching/print-types'
import { toS3ObjectsForPrint } from '@/components/screens/order-matching/print-types'
import { toSteelOrderViewModel } from '@/components/screens/order-matching/types'
import { UnmatchedMillSheets } from '@/components/screens/order-matching/unmatched-mill-sheets'
import type { UpdateOrderNumberResult } from '@/components/screens/order-matching/unmatched-millsheet-types'
import { toUnmatchedMillsheetViewModel } from '@/components/screens/order-matching/unmatched-millsheet-types'
import { Button } from '@/components/ui/button'
import type { DataTableSorter } from '@/components/ui/data-table/data-table'
import { useFilterBarState } from '@/components/ui/filter-bar'
import { PageHeader } from '@/components/ui/page-header'
import { ScreenTabs } from '@/components/ui/screen-tabs'
import { useFilterScopedPage } from '@/components/ui/use-filter-scoped-page'
import { runMutation } from '@/lib/run-mutation'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'
import { useMe } from '@/providers/me-context'

export const ORDER_MATCHING_TABS = [
  { id: 'orders', label: '発注一覧' },
  { id: 'unlinked', label: '未紐付けミルシート一覧' },
  { id: 'history', label: '取込履歴' },
] as const

export type OrderMatchingTab = (typeof ORDER_MATCHING_TABS)[number]['id']

// URL の ?tab= は利用者が書き換えられるため、未知の値は既定の発注一覧に倒す
export const parseOrderMatchingTab = (value: unknown): OrderMatchingTab =>
  ORDER_MATCHING_TABS.find((tab) => tab.id === value)?.id ?? 'orders'

const PAGE_SIZE = 100

const presentOrNull = (value: string) => (isPresent(value.trim()) ? value.trim() : null)

// 発注照合（発注一覧・未紐付けミルシート一覧・取込履歴の3タブ）のコンテナ。タブは URL（?tab=）と連動する。
// GraphQL のクエリ・ミューテーション配線を担い、画面コンポーネントへ props として渡す。
// route（routes/t/$tenant_id/order_matching/index.tsx）から切り出しているのは、storybook の
// 擬似e2e story が route ファイルを経由せずにコンテナごとマウントできるようにするため
// （millsheet-home-container.tsx と同じ理由・同じ分担。機能フラグのガードとサイドバーは route 側の責務）
export function OrderMatchingContainer({
  tab,
  onChangeTab,
  onNavigateToImport,
}: {
  tab: OrderMatchingTab
  onChangeTab: (tab: OrderMatchingTab) => void
  onNavigateToImport: () => void
}) {
  const me = useMe()
  const client = api.useClient()

  // 詳細から戻る・リロード・URL 共有で条件を保つため URL に同期する。同じ画面の 2 つのタブは一覧ごとの urlKey で分ける
  const [searchState, setSearchState] = useFilterBarState<SteelOrderSearchState>(EMPTY_STEEL_ORDER_SEARCH_STATE, {
    syncUrl: true,
    urlKey: 'steelOrdersFilter',
    parse: parseSteelOrderSearchState,
  })
  // 入力欄に出ている条件がまだ一覧に反映されていない間は、古い一覧のまま一括操作させない
  const [filterPending, setFilterPending] = useState(false)
  const [unlinkedSearch, setUnlinkedSearch] = useFilterBarState<MillsheetSearch>(EMPTY_MILLSHEET_SEARCH, {
    syncUrl: true,
    urlKey: 'unlinkedMillsheetsFilter',
    parse: parseMillsheetSearch,
  })
  // 条件が URL 経由（ブラウザの戻る/進む）で変わったときも 1 ページ目へ戻す
  const [page, setPage] = useFilterScopedPage(searchState)
  const [unlinkedPage, setUnlinkedPage] = useFilterScopedPage(unlinkedSearch)
  const [unlinkedSorters, setUnlinkedSorters] = useState<DataTableSorter[]>([])
  const [csvImportsPage, setCsvImportsPage] = useState(1)
  // 複数列ソート。空配列はソート未選択（バックエンドの既定=importedAt降順に任せる。
  // ミルシート等の他テーブルと同じ初期状態）を表す
  const [sorters, setSorters] = useState<DataTableSorter[]>([])
  const steelOrdersInput = useMemo((): SteelOrdersSearchInput => {
    return {
      freeWord: presentOrNull(searchState.freeWord),
      referenceNumber: presentOrNull(searchState.referenceNumber),
      category: searchState.category,
      // 画面の「対象外」はステータス値ではなくフラグ。通常ステータス指定時は対象外の行
      // （保存ステータスが凍結された行）を除外するため isMillsheetExcluded: false を併せて送る（v1 と同じ）
      millsheetStatus: searchState.millsheetStatus === 'excluded' ? null : searchState.millsheetStatus,
      isMillsheetExcluded:
        searchState.millsheetStatus === 'excluded' ? true : isSome(searchState.millsheetStatus) ? false : null,
      orderStatus: searchState.orderStatus,
      qualityCheckStatus: searchState.qualityCheckStatus,
      supplierName: presentOrNull(searchState.supplierName),
      managementNumber: presentOrNull(searchState.managementNumber),
      constructionNumber: presentOrNull(searchState.constructionNumber),
      constructionName: presentOrNull(searchState.constructionName),
      productName: presentOrNull(searchState.productName),
      standard: presentOrNull(searchState.standard),
      manufacturerName: presentOrNull(searchState.manufacturerName),
      outputDateFrom: searchState.outputDateFrom,
      outputDateTo: searchState.outputDateTo,
      importedAtFrom: searchState.importedAtFrom,
      importedAtTo: searchState.importedAtTo,
      // sorters が空のときは sorts を送らず、バックエンドの既定（importedAt 降順）に任せる
      sorts: sorters.length > 0 ? toSteelOrderSorts(sorters) : undefined,
      page,
      pageSize: PAGE_SIZE,
    }
  }, [searchState, sorters, page])

  // ページ以外の検索条件のシリアライズ。変わったら OrdersTable 側が全件選択状態を破棄する
  const conditionsKey = useMemo(() => JSON.stringify({ ...steelOrdersInput, page: null }), [steelOrdersInput])

  const steelOrdersQuery = api.useFetchSteelOrdersQuery({ variables: { input: steelOrdersInput } })
  // 条件変更によるロード中も直前の一覧を出したままにする（テーブルが一瞬空になるチラつき防止。v1 と同じ）
  const steelOrdersResult = steelOrdersQuery.data?.steelOrders ?? steelOrdersQuery.previousData?.steelOrders
  // steelOrdersResult は毎レンダー新しい参照になり得るため、view model への変換自体を明示的にメモ化する
  const orders = useMemo(() => steelOrdersResult?.steelOrders.map(toSteelOrderViewModel) ?? [], [steelOrdersResult])

  const handleSearchChange = (next: SteelOrderSearchState) => {
    setSearchState(next)
    // 条件が変わったら1ページ目へ戻す（旧条件のページ位置が新結果に存在しないことがあるため）
    setPage(1)
  }

  // 印刷ダイアログ用（PR E）。一覧クエリの millsheets は表示に必要な最小項目のみのため、
  // 印刷（集計表・原本PDF）に必要な明細・PDF情報まで含めて選択（全件選択含む）のidsで取り直す
  // （OrdersTable の「印刷」ボタン押下時に呼ばれる。v1 SteelOrderMillsheetPrintForm と同じ再取得方針）
  const onFetchPrintOrders = async (ids: string[]): Promise<SteelOrderForPrint[]> => {
    // ids が200件（サーバの MAX_PAGE_SIZE）を超え得るため分割して取り、参照番号順に並べ直す
    const results = await Promise.all(
      chunk(ids, 200).map(
        async (chunkIds) =>
          await client.fetchSteelOrdersForPrint({
            input: { ids: chunkIds, sortKey: 'referenceNumber', sortOrder: 'asc', pageSize: 200 },
          }),
      ),
    )
    return sortBy(
      results.flatMap((result) => result.steelOrders.steelOrders),
      [(order) => order.referenceNumber],
    )
  }

  const onFetchPrintImages = async (s3ObjectIds: string[]): Promise<S3ObjectForPrint[]> => {
    if (s3ObjectIds.length === 0) {
      return []
    }
    const result = await client.fetchPagedS3Objects({ ids: s3ObjectIds })
    return toS3ObjectsForPrint(result.pagedS3Objects.s3Objects)
  }

  const onCountAllResults = async (filter: SteelOrdersSearchInput): Promise<number | null> => {
    try {
      const result = await client.fetchSteelOrders({ input: { ...filter, page: 1, pageSize: 1 } })
      return result.steelOrders.totalCount
    } catch {
      return null
    }
  }

  // 印刷だけはID列挙が必要。OrdersTableで300件以下を確認した後に限り、通常の一覧queryを
  // MAX_PAGE_SIZE単位で取得するため、大量件数の全ID取得経路にはならない。
  const onFetchAllResultIdsForPrint = async (filter: SteelOrdersSearchInput): Promise<string[] | null> => {
    try {
      const pageSize = 200
      const baseInput = { ...filter, pageSize }
      const first = await client.fetchSteelOrders({ input: { ...baseInput, page: 1 } })
      const totalPages = Math.ceil(first.steelOrders.totalCount / pageSize)
      const rest = await Promise.all(
        range(2, totalPages + 1).map(
          async (printPage) => await client.fetchSteelOrders({ input: { ...baseInput, page: printPage } }),
        ),
      )
      return [first, ...rest].flatMap((result) => result.steelOrders.steelOrders.map((order) => order.id))
    } catch {
      return null
    }
  }

  // 一括操作・セル編集共通の更新処理。成否だけを画面側へ返し、成功・失敗のメッセージ組み立ては
  // 画面コンポーネント（OrdersTable）側の責務にする（GraphQL を画面に持ち込まないため）
  const updateSteelOrders = async (
    target: SteelOrderBulkTarget,
    input: { isMillsheetExcluded?: boolean; outputDate?: string | null },
  ): Promise<boolean> => {
    try {
      await client.updateSteelOrders({
        input: {
          target,
          ...(input.isMillsheetExcluded === undefined ? {} : { isMillsheetExcluded: input.isMillsheetExcluded }),
          ...(input.outputDate === undefined ? {} : { outputDate: input.outputDate }),
        },
      })
      await steelOrdersQuery.refetch()
      return true
    } catch {
      return false
    }
  }

  // 明細展開行の品管照合ステータス更新（20260907_01: 一覧側にも列を追加）。
  // 発注詳細画面（order-detail-container.tsx）の onUpdateItemQualityCheckStatus と同じく、
  // runMutation（トースト付き）+ 一覧クエリの refetch で親行のバッジも再描画させる
  const onUpdateItemQualityCheckStatus = async (
    steelOrderItemId: string,
    qualityCheckStatus: SteelOrderQualityCheckStatusEnum,
  ): Promise<boolean> =>
    await runMutation({
      mutate: async () => {
        await client.updateSteelOrderItemQualityCheckStatus({ steelOrderItemId, qualityCheckStatus })
      },
      refetch: async () => {
        await steelOrdersQuery.refetch()
      },
      success: { message: '品管照合ステータスを更新しました' },
      failure: { message: '更新に失敗しました' },
    })

  // 未紐付けミルシート一覧タブ・発注データ登録画面（v1 の UnlinkedMillsheetsTable / UploadSteelOrderCsvForm 相当）。
  // v1 と同じく変換マスタ（表示用の名寄せ）を使う。ミルシート側の品名・サイズ・長さを
  // 発注データ側の表記に変換して表示する（照合には使わず表示のみ）
  const unlinkedInput = useMemo(
    (): MillsheetsSearchInput => ({
      ...unlinkedSearch,
      sorts: toMillsheetSortInputs(unlinkedSorters),
      page: unlinkedPage,
      pageSize: PAGE_SIZE,
    }),
    [unlinkedPage, unlinkedSearch, unlinkedSorters],
  )
  const unlinkedMillsheetsQuery = api.useFetchPagedUnlinkedMillsheetsQuery({ variables: { input: unlinkedInput } })
  // 絞り込み・ページ送りのたびに data が一瞬 undefined に戻るため、直前の結果を出したままにする
  // （発注一覧タブと同じ理由・同じ形。ここだけ previousData を見ておらず、skeleton→実データの
  // 2段階シフトが起きていた。2026/08 レビューFB）
  const unlinkedMillsheetsResult = unlinkedMillsheetsQuery.data ?? unlinkedMillsheetsQuery.previousData
  const csvImportsQuery = api.useFetchPagedSteelOrderCsvImportsQuery({
    variables: { page: csvImportsPage, pageSize: PAGE_SIZE },
  })
  // 取込履歴タブも同じ理由で previousData フォールバックを揃える（unlinkedMillsheetsResult と同様。
  // 2026/08 レビューFB「他にも previousData を見ていない一覧が無いか確認して揃える」対応）
  const csvImportsResult = csvImportsQuery.data ?? csvImportsQuery.previousData
  const shapeNameConversionsQuery = api.useFetchShapeNameConversionsQuery()
  const unlinkedMillsheetsFilterOptionsQuery = api.useFetchUnlinkedMillsheetsFilterOptionsQuery()
  const shapeNameConversionMap = useMemo(
    () => buildShapeNameConversionDisplayMap(shapeNameConversionsQuery.data?.shapeNameConversions ?? []),
    [shapeNameConversionsQuery.data],
  )
  const unmatchedMillsheets = useMemo(
    () =>
      (unlinkedMillsheetsResult?.pagedUnlinkedMillsheets.millsheets ?? []).map((millsheet) =>
        toUnmatchedMillsheetViewModel(millsheet, shapeNameConversionMap),
      ),
    [unlinkedMillsheetsResult, shapeNameConversionMap],
  )

  // ミルシート・発注データの取込や紐付けの変更は発注一覧と未紐付け一覧の両方に影響するため、
  // 関係する3タブ分のクエリをまとめて取り直す（v1 の refetchAll と同じ）
  const refetchOrderMatchingData = async () => {
    await Promise.all([steelOrdersQuery.refetch(), unlinkedMillsheetsQuery.refetch(), csvImportsQuery.refetch()])
  }

  const onBulkDelete = async (target: SteelOrderBulkTarget): Promise<boolean> => {
    try {
      await client.deleteSteelOrdersByTarget({ target })
      // 削除された発注に紐付いていたミルシートは未紐付けに戻るため、発注一覧だけでなく
      // 未紐付け一覧・取込履歴も取り直す（v1 の refetchAll と同じ）
      await refetchOrderMatchingData()
      return true
    } catch {
      return false
    }
  }

  const onUpdateOrderNumber = async (millsheetId: string, orderNumber: string): Promise<UpdateOrderNumberResult> => {
    try {
      const result = await client.updateMillsheetOrderNumber({ millsheetId, orderNumber })
      // 紐付き先（発注側）も変わるため、未紐付け一覧だけでなく発注一覧も取り直す
      await Promise.all([unlinkedMillsheetsQuery.refetch(), steelOrdersQuery.refetch()])
      if (isSome(result.data?.updateMillsheetOrderNumber.steelOrderId)) {
        return { status: 'linked' }
      }
      if (orderNumber === '') {
        return { status: 'clearedUnlinked' }
      }
      // 複合キー衝突（同じ証明書番号・工事名称・注文番号が既に存在する）はサーバーが理由入りの
      // ValidationError を返す。該当する発注が無いだけの正常系とは別の分岐にはしないが、
      // メッセージはここで区別せず catch 側で拾う（サーバーがエラーとして返すため）
      return { status: 'savedUnmatched' }
    } catch (error) {
      return { status: 'error', message: userFacingErrorMessage(error, '紐付けの保存に失敗しました') }
    }
  }

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-6 lg:px-8">
      <PageHeader
        className="mb-6"
        title="発注照合"
        description="発注データとミルシートの紐付け状況を確認・管理します。"
        actions={
          <Button size="lg" data-testid="order-matching-navigate-import" onClick={onNavigateToImport}>
            <Upload />
            発注データ登録
          </Button>
        }
      />

      <ScreenTabs
        items={ORDER_MATCHING_TABS.map((item) => ({ id: item.id, label: item.label }))}
        value={tab}
        onChange={onChangeTab}
        variant="underline"
        aria-label="発注照合タブ"
        className="mb-6"
      />

      {tab === 'orders' &&
        (isSome(steelOrdersQuery.error) ? (
          <GraphqlErrorPanel
            error={steelOrdersQuery.error}
            subject="発注一覧"
            layout="section"
            onRetry={() => {
              void steelOrdersQuery.refetch()
            }}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <FilterBar value={searchState} onChange={handleSearchChange} onPendingChange={setFilterPending} />
            <OrdersTable
              orders={orders}
              loading={steelOrdersQuery.loading}
              pagination={{ page, pageSize: PAGE_SIZE, totalCount: steelOrdersResult?.totalCount ?? 0 }}
              sorters={sorters}
              onChangeSorters={(next) => {
                setSorters(next)
                // ソート変更時は1ページ目へ戻す（見ていたページ位置は新しい並びでは意味を持たないため）
                setPage(1)
              }}
              onChangePage={setPage}
              filter={steelOrdersInput}
              onBulkExclude={async (target) => await updateSteelOrders(target, { isMillsheetExcluded: true })}
              onBulkInclude={async (target) => await updateSteelOrders(target, { isMillsheetExcluded: false })}
              onUpdateOutputDate={async (target, outputDate) => await updateSteelOrders(target, { outputDate })}
              onUpdateItemQualityCheckStatus={onUpdateItemQualityCheckStatus}
              onBulkDelete={onBulkDelete}
              shapeNameConversionMap={shapeNameConversionMap}
              onFetchPrintOrders={onFetchPrintOrders}
              onFetchPrintImages={onFetchPrintImages}
              conditionsKey={conditionsKey}
              actionsDisabled={filterPending}
              onCountAllResults={onCountAllResults}
              onFetchAllResultIdsForPrint={onFetchAllResultIdsForPrint}
            />
          </div>
        ))}

      {tab === 'unlinked' &&
        (isSome(unlinkedMillsheetsQuery.error) ||
        isSome(shapeNameConversionsQuery.error) ||
        isSome(unlinkedMillsheetsFilterOptionsQuery.error) ? (
          <GraphqlErrorPanel
            error={
              unlinkedMillsheetsQuery.error ??
              shapeNameConversionsQuery.error ??
              unlinkedMillsheetsFilterOptionsQuery.error
            }
            subject="未紐付けミルシート一覧"
            layout="section"
            onRetry={() => {
              void unlinkedMillsheetsQuery.refetch()
              void shapeNameConversionsQuery.refetch()
              void unlinkedMillsheetsFilterOptionsQuery.refetch()
            }}
          />
        ) : (
          <UnmatchedMillSheets
            millsheets={unmatchedMillsheets}
            search={unlinkedSearch}
            millsheetSortConfig={me.tenant.setting?.millsheetSortConfig ?? {}}
            filterOptions={
              unlinkedMillsheetsFilterOptionsQuery.data?.unlinkedMillsheetsFilterOptions ??
              EMPTY_MILLSHEET_FILTER_OPTIONS
            }
            loading={unlinkedMillsheetsQuery.loading}
            pagination={{
              page: unlinkedPage,
              pageSize: PAGE_SIZE,
              totalCount: unlinkedMillsheetsResult?.pagedUnlinkedMillsheets.totalCount ?? 0,
            }}
            onChangePage={setUnlinkedPage}
            onChangeSearch={(nextSearch, nextSorters) => {
              setUnlinkedSearch(nextSearch)
              setUnlinkedSorters(nextSorters)
              setUnlinkedPage(1)
            }}
            onUpdateOrderNumber={onUpdateOrderNumber}
          />
        ))}

      {tab === 'history' &&
        (isSome(csvImportsQuery.error) ? (
          <GraphqlErrorPanel
            error={csvImportsQuery.error}
            subject="取込履歴"
            layout="section"
            onRetry={() => {
              void csvImportsQuery.refetch()
            }}
          />
        ) : (
          <ImportHistoryTable
            csvImports={csvImportsResult?.pagedSteelOrderCsvImports.steelOrderCsvImports ?? []}
            loading={csvImportsQuery.loading}
            pagination={{
              page: csvImportsPage,
              pageSize: PAGE_SIZE,
              totalCount: csvImportsResult?.pagedSteelOrderCsvImports.totalCount ?? 0,
            }}
            onChangePage={setCsvImportsPage}
          />
        ))}
    </div>
  )
}
